// src/components/Navbar.jsx
import logoImage from '../assets/images/Logo.svg';

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ArrowBackIosNewRounded as BackIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = ({ sidebarItems = [] }) => {
  // State management
  const [mobileOpen, setMobileOpen] = useState(false);

  // Theme and navigation hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const canGoBack = (() => {
    try {
      const idx = window.history?.state?.idx;
      if (typeof idx === 'number') return idx > 0;
      return window.history.length > 1;
    } catch {
      return false;
    }
  })();

  const shouldShowBack = isMobile && location?.pathname !== '/';

  const handleBack = () => {
    if (canGoBack) {
      navigate(-1);
      return;
    }
    navigate('/', { replace: true });
  };

  // Color configuration - easily modifiable
  const colors = {
    primary: '#0D1C4F',
    secondary: '#1E3A8A',
    accent: '#016f57',
    light: '#F8FAFC',
    text: '#1F2937',
    textLight: '#6B7280',
    textMuted: '#475569',
    surface: 'rgba(255,255,255,0.82)',
    surfaceBorder: 'rgba(255,255,255,0.52)',
    gradient: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
  };

  const glassStyles = {
    background: 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(248,250,252,0.56) 100%)',
    border: `1px solid ${colors.surfaceBorder}`,
    boxShadow: `0 24px 60px ${colors.primary}22`,
    backdropFilter: 'blur(26px) saturate(165%)',
    WebkitBackdropFilter: 'blur(26px) saturate(165%)'
  };

  // Navigation items configuration - easily modifiable
  const navigationItems = [
    { label: 'Home', path: '/', action: () => navigate('/') },
    { label: 'About', path: '/about', action: () => navigate('/about') },
  ];

  // Authentication actions - easily modifiable
  const authActions = {
    login: () => {
      // console.log('Redirecting to login page');
      navigate('/login');
    }
    // signup: () => {
    //   // console.log('Redirecting to signup page');
    //   navigate('/signup');
    // }
  };

  // Logo configuration - modify path as needed
  const logoConfig = {
    imageSrc: logoImage,
    imageAlt: 'PeerCampus Logo',
    title: 'PeerCampus'
  };

  const containerStyles = {
    px: { xs: 1.5, sm: 2.5, md: 3 },
    maxWidth: { xs: '100%', xl: '1320px' },
    width: '100%',
    mx: 'auto'
  };

  // Combine navigation items with sidebar items for mobile
  // Combined list was unused; sidebarItems handled directly in drawer

  // Logo component — uses anchor semantics for better defaults (right-click, open in new tab possible)
  const LogoComponent = () => {
    return (
      <Box
        component="a"
        href="/"
        onClick={(e) => {
          e.preventDefault();
          navigate('/');
        }}
        aria-label="PeerCampus - go to home"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          textDecoration: 'none',
          outline: 'none',
          border: 'none',
          background: 'transparent',
          p: 0,
          m: 0,
        }}
      >
        <Box
          component="img"
          src={logoConfig.imageSrc}
          alt={logoConfig.imageAlt}
          role="img"
          sx={{
            height: { xs: 36, sm: 42, md: 54 },
            width: 'auto',
            cursor: 'pointer',
            transition: 'transform 0.15s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
            '&:focus-visible': {
              outline: `3px solid ${colors.primary}33`,
              borderRadius: '6px'
            }
          }}
        />
      </Box>
    );
  };

  // Event handlers
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavItemClick = (item) => {
    item.action();
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  // Keyboard handler for mobile list items (Enter / Space)
  const handleListItemKeyDown = (e, item) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNavItemClick(item);
    }
  };

  // Mobile drawer content
  const drawer = (
    <Box
      sx={{
        width: 304,
        height: '100%',
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        overflowY: 'auto',
      }}
      role="presentation"
    >
      {/* Mobile drawer header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: `1px solid ${colors.primary}20`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LogoComponent />
          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary, letterSpacing: '-0.02em' }}>
            {logoConfig.title}
          </Typography>
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          aria-label="Close mobile menu"
          size="large"
          autoFocus
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Mobile navigation items */}
      <List sx={{ pt: 2 }}>
        {/* Main navigation items */}
        <Typography variant="subtitle2" sx={{ px: 3, py: 1, color: colors.textLight }}>
          Main Navigation
        </Typography>
        {navigationItems.map((item) => (
          <ListItem
            key={item.label}
            onClick={() => handleNavItemClick(item)}
            onKeyDown={(e) => handleListItemKeyDown(e, item)}
            role="button"
            tabIndex={0}
            sx={{
              cursor: 'pointer',
              mx: 1,
              borderRadius: '8px',
              minHeight: 48,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: `${colors.primary}08`,
              },
              '&:focus-visible': {
                outline: `3px solid ${colors.primary}33`,
                bgcolor: `${colors.primary}04`
              },
              '& .MuiListItemText-primary': {
                fontSize: '1rem'
              }
            }}
          >
            <ListItemText
              primary={item.label}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: 500,
                  color: colors.text,
                }
              }}
            />
          </ListItem>
        ))}

        {/* Sidebar items (only show if there are any) */}
        {sidebarItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ px: 3, py: 1, color: colors.textLight }}>
              Student Portal
            </Typography>
            {sidebarItems.map((item) => (
              <ListItem
                key={item.label}
                onClick={() => handleNavItemClick(item)}
                onKeyDown={(e) => handleListItemKeyDown(e, item)}
                role="button"
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  mx: 1,
                  borderRadius: '8px',
                  minHeight: 48,
                  px: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: `${colors.primary}08`,
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${colors.primary}33`,
                    bgcolor: `${colors.primary}04`
                  },
                  '& .MuiListItemText-primary': {
                    fontSize: '1rem'
                  }
                }}
              >
                {item.icon && <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>}
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: 500,
                      color: colors.text,
                    }
                  }}
                />
              </ListItem>
            ))}
          </>
        )}

        {/* Mobile auth buttons */}
        <Box sx={{ px: 2, pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={authActions.login}
            sx={{
              background: colors.gradient,
              color: '#FFFFFF',
              borderRadius: '999px',
              minHeight: 48,
              boxShadow: `0 16px 30px ${colors.primary}24`,
              border: '1px solid transparent',
              fontWeight: 700,
              '&:hover': {
                background: colors.gradient,
                filter: 'brightness(1.05)',
              },
              '&:focus-visible': {
                outline: `3px solid ${colors.primary}33`,
              },
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            Log in
          </Button>
          {/* <Button
            fullWidth
            variant="contained"
            onClick={authActions.signup}
            sx={{
              background: colors.gradient,
              borderRadius: '8px',
              px: 2,
              py: 'calc(var(--btn-padding-block) * 0.6)',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              minHeight: 44,
              fontSize: '1rem',
              border: '1px solid transparent',
              '&:hover': {
                background: colors.gradient,
              },
              '&:focus-visible': {
                outline: `3px solid ${colors.primary}33`,
              }
            }}
          >
            Sign Up
          </Button> */}
        </Box>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        component="nav"
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          left: 0,
          right: 0,
          top: 0,
          width: '100%',
          zIndex: theme.zIndex.appBar,
        }}
        aria-label="Main site navigation"
      >
        <Container maxWidth={false} sx={{ ...containerStyles, pt: { xs: 1, md: 1.5 } }}>
          <Box
            sx={{
              ...glassStyles,
              position: 'relative',
              overflow: 'hidden',
              borderRadius: { xs: '20px', md: '30px' },
              px: { xs: 1.25, sm: 1.75, md: 2.25 },
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.1) 52%, rgba(255,255,255,0) 100%)',
                pointerEvents: 'none',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 1,
                borderRadius: { xs: '19px', md: '29px' },
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
                pointerEvents: 'none',
              },
            }}
          >
          <Toolbar
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr auto',
                md: '1fr auto 1fr'
              },
              alignItems: 'center',
              gap: { xs: 1, md: 2.5 },
              py: { xs: 0.75, md: 1 },
              px: 0,
              minHeight: { xs: '64px', md: '78px' }
            }}
          >
            {/* Logo Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.25, md: 1.75 },
                gridColumn: { xs: '1 / 2', md: '1 / 2' },
                minWidth: 0,
                width: '100%',
                justifySelf: 'start'
              }}
            >
              {shouldShowBack && (
                <IconButton
                  onClick={handleBack}
                  aria-label="Go back"
                  size="large"
                  edge="start"
                  sx={{
                    color: colors.primary,
                    '&:hover': { backgroundColor: `${colors.primary}08` },
                    '&:focus-visible': { outline: `3px solid ${colors.primary}33`, borderRadius: '10px' },
                  }}
                >
                  <BackIcon fontSize="small" />
                </IconButton>
              )}
              <LogoComponent />

              {/* Title - hide on mobile if space is limited */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  maxWidth: { md: 260 },
                  flexShrink: 1
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: colors.primary,
                    fontFamily: 'Inter, sans-serif',
                    letterSpacing: '-0.04em',
                    lineHeight: 1,
                    fontSize: { sm: '1.55rem', md: '1.95rem' },
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate('/')}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate('/');
                    }
                  }}
                  role="link"
                  aria-label="Go to home"
                >
                  {logoConfig.title}
                </Typography>
                <Chip
                  label="AI-Powered"
                  size="small"
                  sx={{
                    backgroundColor: `${colors.accent}14`,
                    border: `1px solid ${colors.accent}2a`,
                    backdropFilter: 'blur(12px)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.68rem',
                    color: colors.accent,
                    height: 28,
                    px: 1.1,
                    '& .MuiChip-label': {
                      px: 0.2,
                    }
                  }}
                />
              </Box>

              {/* Mobile title */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: colors.primary,
                    letterSpacing: '-0.03em',
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/')}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate('/');
                    }
                  }}
                  role="link"
                  aria-label="Go to home"
                >
                  {logoConfig.title}
                </Typography>
              </Box>
            </Box>

            {/* Centered navigation (desktop) */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gridColumn: { md: '2 / 3' },
                justifySelf: { md: 'center' },
                alignSelf: 'center'
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.75,
                  borderRadius: '999px',
                  backgroundColor: `${colors.primary}08`,
                  border: `1px solid ${colors.primary}12`,
                }}
              >
                {navigationItems.map((item) => {
                  const isActive = location?.pathname === item.path;
                  return (
                    <Button
                      key={item.label}
                      onClick={() => handleNavItemClick(item)}
                      aria-current={isActive ? 'page' : undefined}
                      aria-label={`Navigate to ${item.label}`}
                      sx={{
                        color: isActive ? colors.primary : colors.textMuted,
                        cursor: 'pointer',
                        fontWeight: isActive ? 700 : 600,
                        position: 'relative',
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 2.4,
                        py: 1,
                        minHeight: 42,
                        fontSize: '0.98rem',
                        borderRadius: '999px',
                        backgroundColor: isActive ? colors.surface : 'transparent',
                        boxShadow: isActive ? `0 10px 24px ${colors.primary}12` : 'none',
                        '&:hover': {
                          color: colors.primary,
                          backgroundColor: isActive ? colors.surface : `${colors.primary}08`,
                        },
                        '&:focus-visible': {
                          outline: `3px solid ${colors.primary}33`,
                          borderRadius: '999px',
                          boxShadow: `0 0 0 4px ${colors.primary}10`
                        }
                      }}
                    >
                      <Box component="span" sx={{ fontSize: '1rem' }}>{item.label}</Box>
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Auth CTAs (desktop) - far right */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 1.5,
                alignItems: 'center',
                gridColumn: { md: '3 / 4' },
                justifySelf: { md: 'end' },
                justifyContent: 'flex-end',
                width: '100%',
                pr: { md: 0.5 }
              }}
            >
              <Button
                variant="contained"
                onClick={authActions.login}
                aria-label="Log in"
                sx={{
                  background: colors.gradient,
                  color: '#FFFFFF',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  textTransform: 'none',
                  px: 3,
                  py: 1.15,
                  minHeight: 48,
                  fontSize: '1rem',
                  borderRadius: '999px',
                  boxShadow: `0 18px 34px ${colors.primary}24`,
                  border: '1px solid transparent',
                  '&:hover': {
                    background: colors.gradient,
                    filter: 'brightness(1.05)',
                    boxShadow: `0 20px 38px ${colors.primary}2b`,
                  },
                  '&:focus-visible': { outline: `3px solid ${colors.primary}33`, borderRadius: '999px' }
                }}
              >
                Log in
              </Button>

              {/* <Button
                variant="contained"
                onClick={authActions.signup}
                aria-label="Sign up"
                sx={{
                  background: colors.gradient,
                  borderRadius: 'var(--btn-radius)',
                  px: 2,
                  py: 'calc(var(--btn-padding-block) * 0.6)',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  minHeight: 44,
                  fontSize: 'var(--btn-font-size)',
                  border: '1px solid transparent',
                  '&:hover': { background: colors.gradient },
                  '&:focus-visible': { outline: `3px solid ${colors.primary}33`, borderRadius: 'var(--btn-radius)' }
                }}
              >
                Sign Up
              </Button> */}
            </Box>

            {/* Mobile menu button */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                gridColumn: { xs: '2 / 3' },
                justifySelf: 'end'
              }}
            >
              <IconButton
                color="primary"
                aria-label="Open mobile menu"
                aria-haspopup="true"
                aria-controls="mobile-menu"
                edge="start"
                onClick={handleDrawerToggle}
                aria-expanded={mobileOpen ? 'true' : 'false'}
                sx={{
                  color: colors.primary,
                  backgroundColor: `${colors.primary}08`,
                  border: `1px solid ${colors.primary}12`,
                  '&:hover': {
                    backgroundColor: `${colors.primary}12`,
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${colors.primary}33`,
                    borderRadius: '999px'
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
          </Box>
        </Container>
      </AppBar>

      {/* Mobile drawer */}
      <Box component="nav" aria-label="Mobile navigation">
        <Drawer
          id="mobile-menu"
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
            },
          }}
          PaperProps={{
            'aria-label': 'Mobile navigation drawer',
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Spacer for fixed navbar */}
      <Box sx={{ height: { xs: '80px', md: '112px' } }} />
    </>
  );
};

export default Navbar;